import { convertToUnicode, UnicodeStyle } from 'yay-text';

// German umlauts. `yay-text` has no dedicated bold/italic codepoints for
// these, so it maps them to `<supplementary-plane vowel> + U+0308`
// (combining diaeresis). The Facebook mobile app renderer does not attach
// the combining mark to a surrogate-pair base character, so the umlaut
// appears as an empty box (or the dot jumps to the next glyph).
//
// The workaround is to keep the umlaut as its precomposed ä/ö/ü/Ä/Ö/Ü
// character inside styled runs. Only the umlaut itself loses the fake-bold
// look; the surrounding letters stay styled. `ß` is already passed through
// unchanged by yay-text, so it needs no special handling.
const UMLAUT_CHARS = new Set(['ä', 'ö', 'ü', 'Ä', 'Ö', 'Ü']);

// Exported for the reverse-detection path in the Quill editor: an umlaut
// sitting between styled letters is *conceptually* still part of that
// styled run (the toolbar toggle should treat "𝗞ü𝗻𝘀𝘁𝗹𝗲𝗿" as one bold
// segment, not three). Accepts either the precomposed or the NFD form
// so the caller does not need to normalise the input first.
export function isRendererFriendlyUmlaut(char: string): boolean {
  return UMLAUT_CHARS.has(char.normalize('NFC'));
}

// Only these styles emit the surrogate-pair + combining mark sequence for
// umlauts. MATH_UNDERLINE / MATH_STRIKE keep the umlaut as its ASCII-plane
// base char and just add U+0332 / U+0336, so FB renders them fine.
const STYLES_THAT_DECOMPOSE_UMLAUTS = new Set<UnicodeStyle>([
  UnicodeStyle.MATH_BOLD,
  UnicodeStyle.MATH_ITALIC,
  UnicodeStyle.MATH_BOLD_ITALIC
]);

export function convertToUnicodePreservingUmlauts(
  text: string,
  style: UnicodeStyle
): string {
  if (!STYLES_THAT_DECOMPOSE_UMLAUTS.has(style)) {
    return convertToUnicode(text, style);
  }
  // Compose to NFC first. Otherwise decomposed input like `ü`
  // (common in paste/IME output, and what `convertToUnicode(..., NORMAL)`
  // yields when reversing an already-broken styled umlaut) would split
  // into ["u", "̈"], the "u" would be styled into a surrogate-pair
  // glyph, and the combining diaeresis would dangle — recreating the
  // exact FB-mobile bug this helper exists to avoid.
  return Array.from(text.normalize('NFC'))
    .map((char) =>
      UMLAUT_CHARS.has(char) ? char : convertToUnicode(char, style)
    )
    .join('');
}
